/**
 * Shared `Show details` button used by every UI surface that exposes the
 * connection diagnostics dialog (tree tile, dropdowns, dialogs, banner).
 *
 * Centralises both the styling (so the affordance reads as a real button
 * rather than a text link) and the click → `showConnectionDiagnostics`
 * dispatch so each consumer has a one-liner integration point.
 */

import { Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  showConnectionDiagnostics,
  type ShowConnectionDiagnosticsArgs,
} from '../showConnectionDiagnostics';

type ShowDiagnosticsButtonProps = {
  connectionId: string;
  trigger: ShowConnectionDiagnosticsArgs['trigger'];
  scopeDatabase?: string;
  /** Run before opening the dialog — e.g. close a parent dropdown/popover. */
  beforeOpen?: () => void;
  className?: string;
  label?: string;
  'data-testid'?: string;
};

export function ShowDiagnosticsButton({
  connectionId,
  trigger,
  scopeDatabase,
  beforeOpen,
  className,
  label = 'Show details',
  'data-testid': dataTestId,
}: ShowDiagnosticsButtonProps) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn('h-7 px-2.5 text-xs', className)}
      data-testid={dataTestId}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        beforeOpen?.();
        showConnectionDiagnostics({ connectionId, trigger, scopeDatabase });
      }}
    >
      <Info className="h-3.5 w-3.5" aria-hidden />
      {label}
    </Button>
  );
}
